import { FC, useEffect, useRef, useState } from 'react';
import { Controller, useForm, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { RcFile, UploadFile as UploadFileAntd } from 'antd/es/upload';
import styled from 'styled-components';

import { isHEIC } from '@/entities/vfo/helpers';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useDocuments } from '@/features/vfo/hooks/useDocuments';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { ApiDocumentResponseV2 } from '@/shared/api';
import { breakpoints, media } from '@/shared/components';
import { Alert } from '@/shared/components/Alert';
import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { Button } from '@/shared/uikitV2/Button';
import { files as fileUtils } from '@/shared/utils/files';
import { useModalsStore } from '@/widgets/modals/modals.store';
import {
  UploadVfoDocumentModalProps,
  UploadVfoFormFile,
  UploadVfoFormValues,
} from '@/widgets/modals/types';

import { ModalContainerV1 } from '../../interwill/containers/ModalContainerV1';

import { FormItem } from './FormItem';

type FormFile = {
  title: string;
  document: UploadFileAntd<RcFile> | null;
  case: string;
  country?: string;
};

export type VfoFileFormParams = {
  files: FormFile[];
  message?: string;
};

export const ACCEPT_FILE_TYPES = [
  'application/pdf',
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/tiff',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.heic',
  '.heif',
];

export const UploadVfoDocumentModal: FC<UploadVfoDocumentModalProps> = ({
  caseId,
  isChat,
  message,
  onFinish,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'uploadVfoDocument' });

  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const modals = useModalsStore();
  const { currentUser } = useMembers();
  const { cases, getCaseMemberById } = useCases();
  const { addDocument } = useDocuments();

  const containerRef = useRef<HTMLDivElement>(null);
  const formItemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const {
    control,
    handleSubmit: formHandleSubmit,
    watch,
    setValue,
  } = useForm<VfoFileFormParams>({
    defaultValues: {
      files: [],
      message,
    },
  });

  const onSubmit = async (values: VfoFileFormParams) => {
    setIsLoading(true);
    const results = await Promise.allSettled(
      values.files.map(async (item) => {
        if (!item.document?.originFileObj || !currentUser?.profileId || !item.document.size)
          return Promise.reject();

        const uploaderCaseMember = getCaseMemberById(item.case, currentUser?.profileId);

        if (!uploaderCaseMember?.profileId) return Promise.reject();

        const content = await fileUtils.toBase64(item.document?.originFileObj);
        if (!content) return Promise.reject();

        return addDocument({
          title: item.title,
          fileContent: content,
          country: item.country,
          fileName: item.document?.name,
          fileSizeBytes: item.document.size,
          fileType: isHEIC(item.document.originFileObj)
            ? 'image/heic'
            : item.document.originFileObj.type,
          source: 'UPLOAD',
          uploaderProfileId: uploaderCaseMember.profileId,
          caseIds: [item.case],
        });
      }),
    );
    setIsLoading(false);

    const hasErrors = results.some(
      (result) => result.status === 'rejected' || (result.status === 'fulfilled' && !result.value),
    );

    if (hasErrors) return;

    const filesRes = values.files.map((item, i) => ({
      ...item,
      filePath: results[i].status === 'fulfilled' ? results[i].value?.filePath : undefined,
    }));

    const addedDocuments = results
      .filter((result): result is PromiseFulfilledResult<ApiDocumentResponseV2> => {
        return result.status === 'fulfilled' && Boolean(result.value);
      })
      .map((result) => result.value);

    const formValues: UploadVfoFormValues = {
      files: filesRes.filter(
        (item): item is UploadVfoFormFile => typeof item.filePath === 'string',
      ),
      message: values.message,
    };

    modals.closeModal();
    onFinish?.(addedDocuments, formValues);
  };

  const files = watch('files') ?? [];

  const showUploadWarning = files.length >= 10;

  const onAddFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ACCEPT_FILE_TYPES.join(',');
    input.multiple = true;
    input.click();
    input.onchange = (e) => {
      const uploadedFiles = (e.target as HTMLInputElement).files;
      if (!uploadedFiles || uploadedFiles.length === 0) return;
      const uploadedFilesArray = Array.from(uploadedFiles)
        .slice(0, 10)
        .map((file, i) => {
          const originFileObj = new File([file], file.name, {
            type: file.type,
            lastModified: file.lastModified,
          }) as RcFile;
          originFileObj.uid = `${Date.now()}_${i}_orig`;

          const document: UploadFileAntd<RcFile> = {
            name: file.name,
            originFileObj,
            uid: `${Date.now()}_${i}`,
            size: file.size,
            percent: 100,
            type: file.type,
            status: 'done',
          };

          return {
            title: '',
            document,
            case: caseId ?? '',
          };
        });

      setValue('files', [...(files ?? []), ...uploadedFilesArray]);

      setIsReady(true);
      input.remove();

      const newFiles = [...(files ?? []), ...uploadedFilesArray];
      const firstIncompleteIndex = newFiles.findIndex((item) => !item.title || !item.case);

      queueMicrotask(() => {
        if (firstIncompleteIndex !== -1 && formItemRefs.current[firstIncompleteIndex]) {
          formItemRefs.current[firstIncompleteIndex]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        } else {
          containerRef.current?.scrollTo({
            top: containerRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }
      });
    };

    const cancelFn = () => {
      if (!files?.length) {
        modals.closeModal();
      }
    };
    input.addEventListener('cancel', cancelFn);
  };

  useEffect(() => {
    if (window.matchMedia('(min-width: 992px)').matches) {
      onAddFile();
    } else {
      setIsReady(true);
    }
  }, []);

  const onSetValues: UseFormSetValue<VfoFileFormParams> = (field, values) => {
    setValue(field, values);

    if (field === 'files' && values.length === 0) {
      modals.closeModal();
    }
  };

  if (!isReady) return null;

  return (
    <ModalContainerV1
      title={
        isChat
          ? t(files?.length > 1 ? 'titlesCasePlural' : 'titlesCase', {
              number: files.length,
            })
          : t('title')
      }
    >
      <FormStyled onSubmit={formHandleSubmit(onSubmit)}>
        <Container ref={containerRef}>
          {showUploadWarning && <Alert color="yellow" text={t('uploadWarning')} />}
          <FormItems>
            {files?.map((file, i) => (
              <FormItem
                key={file.document?.uid}
                index={i}
                control={control}
                cases={cases}
                files={files}
                setValue={onSetValues}
                ref={(el: HTMLDivElement | null) => (formItemRefs.current[i] = el)}
                caseId={caseId}
              />
            ))}
            {(!files || files?.length === 0) && (
              <FormItem
                index={0}
                control={control}
                cases={cases}
                setValue={onSetValues}
                files={files}
                ref={(el: HTMLDivElement | null) => (formItemRefs.current[0] = el)}
                caseId={caseId}
              />
            )}
          </FormItems>
        </Container>
        <Footer>
          {isChat && (
            <Controller
              name={`message`}
              control={control}
              render={({ field }) => {
                return (
                  <Textarea rows={2} placeholder={t(`fields.message.placeholder`)} {...field} />
                );
              }}
            />
          )}

          <Buttons>
            <ButtonAdd
              onClick={onAddFile}
              style={{
                marginRight: 'auto',
                fontSize: 13,
              }}
              type="text"
              icon={<Icon name="paperclip" size="s20" />}
            >
              {t('addFile')}
            </ButtonAdd>
            <Button type="default" size="middle" onClick={modals.closeModal}>
              {t('cancel')}
            </Button>
            <Button
              htmlType="submit"
              size="middle"
              disabled={isLoading || files.length > 10}
              loading={isLoading}
            >
              {t(isChat ? 'send' : 'add')}
            </Button>
          </Buttons>
        </Footer>
      </FormStyled>
    </ModalContainerV1>
  );
};

const FormItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  padding: 24px;
  background: ${COLORS.systemLight200};
  height: 100%;
  max-height: calc(100svh - 180px - 84px);

  ${media.md}, ${breakpoints.sm} {
    max-height: calc(100svh - 180px - 170px);
    padding: 16px;
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column-reverse;
  }
`;

const Footer = styled.div`
  padding: 16px 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${media.md}, ${breakpoints.sm} {
    margin-top: auto;
  }
`;

export const Textarea = styled.textarea`
  height: 60px;
  border-radius: 24px;
  padding: 18px 16px;
  border: none;
  background: none;
  outline: none;
  resize: none;
  border: 1px solid ${COLORS.systemDark100};
`;

const FormStyled = styled.form`
  height: 100%;

  ${media.md}, ${breakpoints.sm} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`;

const ButtonAdd = styled(Button)`
  ${media.md}, ${breakpoints.sm} {
    order: -1;
    margin: 0 auto;
  }
`;
